import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/pagos.css";
import PagosSummary from "../components/PagosSummary";
import PagosFilters from "../components/PagosFilters";
import PagosTable from "../components/PagosTable";
import PagoDetailPanel from "../components/PagoDetailPanel";
import PagoFormModal from "../components/PagoFormModal";
import PagosCobrosTable from "../components/PagosCobrosTable";
import DeudaDetailPanel from "../components/DeudaDetailPanel";
import CargoFormModal from "../components/CargoFormModal";
import PagoEmpresaModal from "../components/PagoEmpresaModal";
import CuentaPorCobrarModal from "../components/CuentaPorCobrarModal";
import CuentasPorCobrarTable from "../components/CuentasPorCobrarTable";
import { usePermissions } from "../../../../utils/permissions";
import usePagosCobros from "../hooks/usePagosCobros";
import useCargosAdicionales from "../hooks/useCargosAdicionales";
import useCuentasPorCobrar from "../hooks/useCuentasPorCobrar";

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function PagosView({
  loading,
  error,
  resumen,
  filtros,
  setFiltros,
  limpiarFiltros,
  lastUpdated,
  page,
  pageSize,
  totalPages,
  total,
  goToPage,
  pagosPaginados,
  seleccionado,
  seleccionar,
  reload,
  form,
  comprobante,
  comprobanteLoading,
  onFetchComprobante,
}) {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("pagos.crear");
  const [activeTab, setActiveTab] = useState("cobros");
  const [cargoModalOpen, setCargoModalOpen] = useState(false);
  const [cargoSubmitting, setCargoSubmitting] = useState(false);
  const [cargoModalPrefill, setCargoModalPrefill] = useState(null);
  const [deudaSeleccionada, setDeudaSeleccionada] = useState(null);
  const [pagoEmpresaModal, setPagoEmpresaModal] = useState({ open: false, mode: "crear", pagoEmpresaId: null });
  const [cxcModal, setCxcModal] = useState({ open: false, mode: "crear", cuentaId: null });
  const [registrarDropdownOpen, setRegistrarDropdownOpen] = useState(false);
  const registrarDropdownRef = useRef(null);
  const cuentasPorCobrar = useCuentasPorCobrar();

  useEffect(() => {
    if (!registrarDropdownOpen) return;
    const handler = (e) => {
      if (registrarDropdownRef.current && !registrarDropdownRef.current.contains(e.target)) {
        setRegistrarDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [registrarDropdownOpen]);

  const {
    cargos,
    reload: reloadCargos,
    crear: crearCargo,
    anular: anularCargo,
  } = useCargosAdicionales();

  // Mapa matricula_id -> cantidad y monto total de cargos pendientes
  const cargosCountMap = useMemo(() => {
    const map = {};
    for (const c of cargos) {
      map[c.matricula_id] = (map[c.matricula_id] || 0) + 1;
    }
    return map;
  }, [cargos]);

  const cargosMontoPorMatricula = useMemo(() => {
    const map = {};
    for (const c of cargos) {
      map[c.matricula_id] = (map[c.matricula_id] || 0) + Number(c.monto || 0);
    }
    return map;
  }, [cargos]);

  const {
    deudas,
    deudasPaginadas,
    loading: loadingDeudas,
    error: errorDeudas,
    lastUpdated: lastUpdatedDeudas,
    resumen: resumenCobros,
    search: searchCobros,
    setSearch: setSearchCobros,
    page: pageCobros,
    pageSize: pageSizeCobros,
    totalPages: totalPagesCobros,
    total: totalCobros,
    goToPage: goToPageCobros,
    reload: reloadDeudas,
  } = usePagosCobros({ cargosCountMap });

  const onAnularPago = useCallback(() => {
    seleccionar(null);
    reload();
    reloadDeudas();
  }, [seleccionar, reload, reloadDeudas]);

  // Cargos del estudiante seleccionado actualmente
  const cargosDeudaSeleccionada = useMemo(() => {
    if (!deudaSeleccionada) return [];
    return cargos.filter((c) => Number(c.matricula_id) === Number(deudaSeleccionada.matriculaId));
  }, [cargos, deudaSeleccionada]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (form.modal) document.body.classList.add("modal-open");
    else document.body.classList.remove("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [form.modal]);

  useEffect(() => {
    if (!form.lastCreatedAt) return;
    reloadDeudas();
    reloadCargos();
  }, [form.lastCreatedAt, reloadDeudas, reloadCargos]);

  useEffect(() => {
    if (!deudaSeleccionada) return;
    const updated = deudas.find((item) => item.id === deudaSeleccionada.id);
    if (!updated) {
      setDeudaSeleccionada(null);
      return;
    }
    if (updated !== deudaSeleccionada) {
      setDeudaSeleccionada(updated);
    }
  }, [deudas, deudaSeleccionada]);

  const quickFilter = (key) => {
    switch (key) {
      case "pendientes":
        setFiltros((prev) => ({ ...prev, estado_pago: "pendiente" }));
        break;
      case "parcial":
        setFiltros((prev) => ({ ...prev, estado_pago: "parcial" }));
        break;
      case "pagado":
        setFiltros((prev) => ({ ...prev, estado_pago: "pagado" }));
        break;
      case "hoy": {
        const today = new Date();
        const todayStr = formatInputDate(today);
        setFiltros((prev) => ({
          ...prev,
          estado_pago: "all",
          fecha_desde: todayStr,
          fecha_hasta: todayStr,
        }));
        break;
      }
      case "mes": {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setFiltros((prev) => ({
          ...prev,
          estado_pago: "all",
          fecha_desde: formatInputDate(first),
          fecha_hasta: formatInputDate(last),
        }));
        break;
      }
      case "total":
      default:
        setFiltros((prev) => ({
          ...prev,
          estado_pago: "all",
          fecha_desde: "",
          fecha_hasta: "",
        }));
        break;
    }
  };

  const todayStr = formatInputDate(new Date());
  const firstMonth = formatInputDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const lastMonth = formatInputDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));

  const activeQuickFilter =
    activeTab === "cobros"
      ? "pendientes"
      : filtros?.fecha_desde === todayStr && filtros?.fecha_hasta === todayStr
        ? "hoy"
        : filtros?.fecha_desde === firstMonth && filtros?.fecha_hasta === lastMonth
          ? "mes"
          : filtros?.estado_pago === "pendiente"
            ? "pendientes"
            : filtros?.estado_pago === "parcial"
              ? "parcial"
              : filtros?.estado_pago === "pagado"
                ? "pagado"
                : "total";

  const lastSyncBase = activeTab === "cobros" ? lastUpdatedDeudas : lastUpdated;
  const lastSync = lastSyncBase ? lastSyncBase.toLocaleString() : "Sincronizacion inicial";

  return (
    <section className="management-page management-theme-violet">
      <div className="management-card management-header mb-4">
        <div className="flex-fill">
          <p className="management-eyebrow mb-1">Financiero / Pagos</p>
          <h4 className="management-title mb-1">Gestion de pagos</h4>
          <p className="text-muted mb-0 d-none d-sm-block">Controla cobros, estados de matricula y referencias.</p>
        </div>
        <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0">
          {canCreate && (
            <div className="position-relative" ref={registrarDropdownRef}>
              <button
                type="button"
                className="btn management-btn-primary dropdown-toggle"
                onClick={() => setRegistrarDropdownOpen((o) => !o)}
              >
                <i className="bi bi-plus-circle me-2" />
                Registrar pago
              </button>
              {registrarDropdownOpen && (
                <ul className="dropdown-menu show shadow-sm" style={{ top: "100%", right: 0, left: "auto", minWidth: 200 }}>
                  <li>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => { setRegistrarDropdownOpen(false); form.openModal(); }}
                    >
                      <i className="bi bi-person me-2 text-muted" />
                      Pago individual
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => { setRegistrarDropdownOpen(false); setPagoEmpresaModal({ open: true, mode: "crear", pagoEmpresaId: null }); }}
                    >
                      <i className="bi bi-building me-2 text-muted" />
                      Pago empresarial
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => { setRegistrarDropdownOpen(false); setCxcModal({ open: true, mode: "crear", cuentaId: null }); }}
                    >
                      <i className="bi bi-receipt me-2 text-muted" />
                      Facturar a crédito (empresa)
                    </button>
                  </li>
                </ul>
              )}
            </div>
          )}
          <small className="text-muted">Actualizado: {lastSync}</small>
        </div>
      </div>

      <PagosSummary
        resumen={{
          ...resumen,
          pendientes: resumenCobros?.total ?? 0,
        }}
        onQuickFilter={(key) => {
          if (key === "pendientes") {
            setActiveTab("cobros");
            return;
          }
          setActiveTab("historico");
          quickFilter(key);
        }}
        activeKey={activeQuickFilter}
      />

      <ul className="nav nav-tabs dashboard-tabs pagos-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "cobros" ? "active" : ""}`}
            onClick={() => setActiveTab("cobros")}
          >
            Cobros pendientes
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "historico" ? "active" : ""}`}
            onClick={() => setActiveTab("historico")}
          >
            Historico de pagos
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === "cuentas-por-cobrar" ? "active" : ""}`}
            onClick={() => setActiveTab("cuentas-por-cobrar")}
          >
            Cuentas por cobrar
          </button>
        </li>
      </ul>

      {activeTab === "cobros" && (
        <>
        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <PagosCobrosTable
              deudas={deudasPaginadas}
              loading={loadingDeudas}
              error={errorDeudas}
              search={searchCobros}
              onSearch={setSearchCobros}
              onSelect={setDeudaSeleccionada}
              selectedId={deudaSeleccionada?.id}
              onReload={reloadDeudas}
              cargosCount={cargosCountMap}
              cargosMonto={cargosMontoPorMatricula}
            />

          </div>
          <div className="col-12 col-lg-4 d-flex">
            <DeudaDetailPanel
              deuda={deudaSeleccionada}
              onClose={() => setDeudaSeleccionada(null)}
              onCobrarSeleccion={({ monto_total_cobro, cargo_ids, primer_concepto_id, includeMatricula, seleccion_items }) => {
                if (!deudaSeleccionada) return;
                form.openModal({
                  matricula_id: deudaSeleccionada.matriculaId,
                  estudiante_id: deudaSeleccionada.estudianteId,
                  monto: monto_total_cobro,
                  lockMatricula: true,
                  lockMonto: false,
                  cargo_ids,
                  monto_total_cobro,
                  seleccion_items,
                  ...(includeMatricula ? {} : { concepto_id: primer_concepto_id }),
                });
              }}
              cargos={cargosDeudaSeleccionada}
              onAnularCargo={anularCargo}
              onNuevoCargo={() => {
                if (!deudaSeleccionada) return;
                setCargoModalPrefill({
                  matricula_id: deudaSeleccionada.matriculaId,
                  estudiante_id: deudaSeleccionada.estudianteId,
                  estudiante: deudaSeleccionada.estudiante,
                  documento: deudaSeleccionada.documento,
                  codigo: deudaSeleccionada.codigoMatricula,
                });
                setCargoModalOpen(true);
              }}
            />
          </div>
        </div>
        <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
          <div className="text-muted small">
            Mostrando {totalCobros === 0 ? 0 : (pageCobros - 1) * pageSizeCobros + 1}-
            {Math.min(totalCobros, pageCobros * pageSizeCobros)} de {totalCobros}
          </div>
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ minHeight: 36, minWidth: 36 }}
              onClick={() => goToPageCobros(pageCobros - 1)}
              disabled={pageCobros <= 1}
            >
              <i className="bi bi-chevron-left" />
            </button>
            <button className="btn btn-outline-secondary btn-sm" style={{ minHeight: 36 }} disabled>
              {pageCobros} / {totalPagesCobros}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ minHeight: 36, minWidth: 36 }}
              onClick={() => goToPageCobros(pageCobros + 1)}
              disabled={pageCobros >= totalPagesCobros}
            >
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
        </>
      )}

      {activeTab === "historico" && (
        <>
          <PagosFilters filtros={filtros} setFiltros={setFiltros} limpiarFiltros={limpiarFiltros} metodos={form?.metodos || []} />

          <div className="row g-3">
            <div className="col-12 col-lg-8">
              <PagosTable
                pagos={pagosPaginados}
                loading={loading}
                error={error}
                onSelect={(pago) => {
                  if (pago?.tipo === "empresarial") {
                    const mode = pago.estado_empresa === "por_cobrar" ? "confirmar" : "aplicar";
                    setPagoEmpresaModal({ open: true, mode, pagoEmpresaId: pago._pagoEmpresaId });
                  } else {
                    seleccionar(pago);
                  }
                }}
                seleccionadoId={seleccionado?.id}
                page={page}
                pageSize={pageSize}
                total={total}
              />
            </div>

            <div className="col-12 col-lg-4 d-flex">
              <PagoDetailPanel
                pago={seleccionado}
                onClose={() => seleccionar(null)}
                comprobante={comprobante}
                comprobanteLoading={comprobanteLoading}
                onFetchComprobante={onFetchComprobante}
                onAnularPago={onAnularPago}
              />
            </div>
          </div>
          <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3">
            <div className="text-muted small">
              Mostrando {total === 0 ? 0 : (page - 1) * pageSize + 1}-{Math.min(total, page * pageSize)} de {total}
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="btn-group">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                  <i className="bi bi-chevron-left" />
                </button>
                <button className="btn btn-outline-secondary btn-sm" disabled>
                  Pagina {page} / {totalPages}
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={reload}>
                <i className="bi bi-arrow-repeat me-2" />
                Recargar
              </button>
            </div>
          </div>
        </>
      )}

      {activeTab === "cuentas-por-cobrar" && (
        <>
          <div className="row g-3">
            <div className="col-12">
              <CuentasPorCobrarTable
                cuentas={cuentasPorCobrar.cuentas}
                loading={cuentasPorCobrar.loading}
                error={cuentasPorCobrar.error}
                onAplicar={(cuentaId) => setCxcModal({ open: true, mode: "aplicar", cuentaId })}
                onCobro={(cuentaId) => setCxcModal({ open: true, mode: "cobro", cuentaId })}
              />
            </div>
          </div>
          <div className="management-card d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
            <div className="text-muted small">
              {cuentasPorCobrar.cuentas.length} cuenta{cuentasPorCobrar.cuentas.length !== 1 ? "s" : ""} por cobrar
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={cuentasPorCobrar.reload}>
              <i className="bi bi-arrow-repeat me-2" />
              Recargar
            </button>
          </div>
        </>
      )}

      <CargoFormModal
        open={cargoModalOpen}
        onClose={() => { setCargoModalOpen(false); setCargoModalPrefill(null); }}
        conceptos={form?.conceptos || []}
        submitting={cargoSubmitting}
        matriculaPrefill={cargoModalPrefill}
        onSubmit={async (payload) => {
          setCargoSubmitting(true);
          try {
            await crearCargo(payload);
            setCargoModalOpen(false);
            setCargoModalPrefill(null);
            const { showToast } = await import("../../../../utils/showToast");
            showToast({ type: "success", message: "Cargo registrado correctamente." });
          } catch (err) {
            const { showToast } = await import("../../../../utils/showToast");
            showToast({ type: "danger", message: err?.response?.data?.message || "Error al registrar el cargo." });
          } finally {
            setCargoSubmitting(false);
          }
        }}
      />

      <PagoFormModal {...form} />

      <PagoEmpresaModal
        open={pagoEmpresaModal.open}
        mode={pagoEmpresaModal.mode}
        pagoEmpresaId={pagoEmpresaModal.pagoEmpresaId}
        onClose={() => setPagoEmpresaModal({ open: false, mode: "crear", pagoEmpresaId: null })}
        onSuccess={() => { reload(); reloadDeudas(); }}
      />

      <CuentaPorCobrarModal
        open={cxcModal.open}
        mode={cxcModal.mode}
        cuentaId={cxcModal.cuentaId}
        onClose={() => setCxcModal({ open: false, mode: "crear", cuentaId: null })}
        onSuccess={() => { cuentasPorCobrar.reload(); reload(); reloadDeudas(); }}
      />
    </section>
  );
}
